import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";

/**
 * V5BotTraderPanel
 * -----------------
 * Signal Engine v5 + Bot Trader Gate görselleştirmesi.
 * Scanner result payload'dan send_to_bot, triggered_strategies, fibonacci_plan
 * bilgilerini okur. Mevcut eski tabloya müdahale etmez.
 */

const formatNumber = (value, digits = 4) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  const num = Number(value);
  if (num === 0) return "0";
  if (Math.abs(num) >= 1000) return num.toFixed(2);
  if (Math.abs(num) >= 1) return num.toFixed(digits);
  return num.toPrecision(digits);
};

const StatusBadge = ({ children, tone = "slate", testId }) => {
  const toneMap = {
    emerald: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    rose: "bg-rose-500/20 text-rose-300 border-rose-500/40",
    amber: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    cyan: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    slate: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    violet: "bg-violet-500/20 text-violet-300 border-violet-500/40",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${toneMap[tone] || toneMap.slate}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
};

export const V5BotTraderPanel = ({ limit = 10, refreshSignal = 0 }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiClient.get("/user/scanner/results", {
          params: { limit },
          timeout: 15000,
        });
        if (cancelled) return;
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        setResults(rows);
      } catch (e) {
        if (cancelled) return;
        setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [limit, refreshSignal]);

  if (loading && results.length === 0) {
    return (
      <section className="rounded border border-slate-800 bg-slate-900 p-4" data-testid="v5-bot-trader-panel-loading">
        <p className="text-xs text-slate-500">Bot Trader v5 yükleniyor…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section className="rounded border border-rose-700 bg-rose-950/40 p-4" data-testid="v5-bot-trader-panel-error">
        <p className="text-xs text-rose-300">Bot Trader v5 hata: {error}</p>
      </section>
    );
  }
  if (!Array.isArray(results) || results.length === 0) {
    return (
      <section className="rounded border border-slate-800 bg-slate-900 p-4" data-testid="v5-bot-trader-panel-empty">
        <div className="mb-2">
          <p className="text-xs uppercase tracking-widest text-slate-400">Bot Trader v5</p>
          <h3 className="text-base font-semibold">Signal Engine + Fibonacci Gate</h3>
        </div>
        <p className="text-xs text-slate-500">Scanner sonucu henüz yok.</p>
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded border border-slate-800 bg-slate-900 p-4" data-testid="v5-bot-trader-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400">Bot Trader v5</p>
          <h3 className="text-base font-semibold">Signal Engine + Fibonacci Gate</h3>
        </div>
        <p className="text-[10px] text-slate-500">
          Aggregate rule: <span className="text-cyan-300">signal_count ≥ 2 AND entry_trigger ≥ 1</span>
        </p>
      </div>

      <div className="overflow-x-auto rounded border border-slate-800">
        <table className="min-w-full text-xs" data-testid="v5-bot-trader-table">
          <thead className="bg-slate-800 text-left uppercase tracking-wider text-[10px] text-slate-400">
            <tr>
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Side</th>
              <th className="px-3 py-2">Bot Gate</th>
              <th className="px-3 py-2">Signal Count</th>
              <th className="px-3 py-2">Signal Score</th>
              <th className="px-3 py-2">Triggered Strategies</th>
              <th className="px-3 py-2">Entry</th>
              <th className="px-3 py-2">SL</th>
              <th className="px-3 py-2">TP1</th>
              <th className="px-3 py-2">TP2</th>
              <th className="px-3 py-2">TP3</th>
              <th className="px-3 py-2">R:R</th>
              <th className="px-3 py-2">Fib Plan</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row) => {
              const snapshot = row?.payload || {};
              const side = String(row?.signal || "none").toLowerCase();
              const sendToBot = Boolean(snapshot?.send_to_bot);
              const triggered = Array.isArray(snapshot?.triggered_strategies) ? snapshot.triggered_strategies : [];
              const longApprovals = Array.isArray(snapshot?.long_approvals) ? snapshot.long_approvals : [];
              const shortApprovals = Array.isArray(snapshot?.short_approvals) ? snapshot.short_approvals : [];
              const approvalCount =
                side === "long" ? longApprovals.length : side === "short" ? shortApprovals.length : 0;
              const signalScore =
                side === "long"
                  ? Number(snapshot?.long_signal_score || 0)
                  : side === "short"
                  ? Number(snapshot?.short_signal_score || 0)
                  : 0;
              const hasEntryTrigger =
                side === "long"
                  ? Boolean(snapshot?.long_has_entry_trigger)
                  : side === "short"
                  ? Boolean(snapshot?.short_has_entry_trigger)
                  : false;
              const fib = snapshot?.fibonacci_plan || {};
              const fibValid = Boolean(fib?.valid);
              const fibReason = String(fib?.reason || "").replace(/_/g, " ");
              const rr = fib?.risk_reward;

              let gateTone = "slate";
              let gateLabel = "N/A";
              if (sendToBot && fibValid) {
                gateTone = "emerald";
                gateLabel = "APPROVED";
              } else if (sendToBot && !fibValid) {
                gateTone = "amber";
                gateLabel = "FIB INVALID";
              } else if (!sendToBot && side !== "none") {
                gateTone = "rose";
                gateLabel = "AGG RULE FAIL";
              }

              return (
                <tr
                  key={row.id}
                  className="border-t border-slate-800 hover:bg-slate-950/60"
                  data-testid={`v5-bot-row-${row.id}`}
                >
                  <td className="px-3 py-2 font-semibold" data-testid={`v5-bot-row-symbol-${row.id}`}>{row.symbol}</td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-side-${row.id}`}>
                    <StatusBadge tone={side === "long" ? "emerald" : side === "short" ? "rose" : "slate"}>
                      {side.toUpperCase()}
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-gate-${row.id}`}>
                    <StatusBadge tone={gateTone}>{gateLabel}</StatusBadge>
                  </td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-count-${row.id}`}>
                    <span className={approvalCount >= 2 ? "text-emerald-300" : "text-slate-400"}>
                      {approvalCount} / 2
                    </span>
                    {hasEntryTrigger ? (
                      <span className="ml-1 text-[9px] text-cyan-300">◉ trigger</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-cyan-300" data-testid={`v5-bot-row-score-${row.id}`}>
                    {signalScore}
                  </td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-strategies-${row.id}`}>
                    {triggered.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {triggered.map((sid) => (
                          <StatusBadge
                            key={sid}
                            tone={
                              // 2026-06-11: 4 entry-trigger stratejisi (cyan), diğerleri violet
                              ["supertrend_flip", "bollinger_squeeze_breakout", "macd_impulse", "ichimoku_trend_continuation"].includes(sid) ? "cyan" : "violet"
                            }
                          >
                            {sid.replace(/_/g, " ")}
                          </StatusBadge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-entry-${row.id}`}>{formatNumber(fib?.entry_price)}</td>
                  <td className="px-3 py-2 text-rose-300" data-testid={`v5-bot-row-sl-${row.id}`}>{formatNumber(fib?.stop_loss)}</td>
                  <td className="px-3 py-2 text-emerald-300" data-testid={`v5-bot-row-tp1-${row.id}`}>{formatNumber(fib?.tp1)}</td>
                  <td className="px-3 py-2 text-emerald-300" data-testid={`v5-bot-row-tp2-${row.id}`}>{formatNumber(fib?.tp2)}</td>
                  <td className="px-3 py-2 text-emerald-300" data-testid={`v5-bot-row-tp3-${row.id}`}>{formatNumber(fib?.tp3)}</td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-rr-${row.id}`}>
                    {rr !== null && rr !== undefined ? (
                      <StatusBadge tone={Number(rr) >= 1.5 ? "emerald" : "rose"}>
                        {Number(rr).toFixed(2)}
                      </StatusBadge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2" data-testid={`v5-bot-row-fib-${row.id}`}>
                    <StatusBadge tone={fibValid ? "emerald" : "rose"}>
                      {fibValid ? "VALID" : fibReason.slice(0, 20) || "—"}
                    </StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <div className="rounded border border-slate-800 bg-slate-950 p-2 text-[11px]" data-testid="v5-legend-entry-triggers">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-400">Entry Trigger (weight 3)</p>
          <div className="flex flex-wrap gap-1">
            <StatusBadge tone="cyan">supertrend flip</StatusBadge>
            <StatusBadge tone="cyan">bollinger squeeze breakout</StatusBadge>
          </div>
        </div>
        <div className="rounded border border-slate-800 bg-slate-950 p-2 text-[11px]" data-testid="v5-legend-confirmations">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-400">Confirmation (weight 2)</p>
          <div className="flex flex-wrap gap-1">
            <StatusBadge tone="violet">ichimoku trend continuation</StatusBadge>
            <StatusBadge tone="violet">macd impulse</StatusBadge>
          </div>
        </div>
        <div className="rounded border border-slate-800 bg-slate-950 p-2 text-[11px]" data-testid="v5-legend-rule">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-400">Send-to-Bot Rule</p>
          <p className="text-slate-300">
            en az <span className="text-cyan-300 font-semibold">2</span> onay
            {" + "}
            en az <span className="text-cyan-300 font-semibold">1</span> entry trigger
            {" + "}
            <span className="text-cyan-300 font-semibold">R:R ≥ 1.5</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default V5BotTraderPanel;
